import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Building2, Store, MapPin, Users, Star, Filter, ArrowUpRight, Clock, UserCheck, AlertTriangle } from 'lucide-react';
import { KPICard } from '../components/ui/KPICard';
import { Card } from '../components/ui/Card';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ZAxis, ComposedChart, Line, Bar, Cell } from 'recharts';
// Level options
const levels = [{
  id: 'konzern',
  name: 'Konzern-Übersicht',
  icon: Building2
}, {
  id: 'markt',
  name: 'Autohäuser',
  icon: Store
}];
const countries = [{
  id: 'deutschland',
  name: 'Deutschland'
}, {
  id: 'usa',
  name: 'USA'
}, {
  id: 'china',
  name: 'China'
}, {
  id: 'uk',
  name: 'Großbritannien'
}, {
  id: 'frankreich',
  name: 'Frankreich'
}, {
  id: 'australien',
  name: 'Australien'
}, {
  id: 'japan',
  name: 'Japan'
}];
// Autohaus data with staff metrics
const marketsData = [{
  id: 'hamburg-niederlassung',
  name: 'BMW Niederlassung Hamburg',
  region: 'Deutschland',
  rating: 4.3,
  staff: 45,
  size: 2800,
  staffPerSqm: 16.1
}, {
  id: 'berlin-welzel',
  name: 'Autohaus Welzel Berlin-Kreuzberg',
  region: 'Deutschland',
  rating: 3.8,
  staff: 18,
  size: 1200,
  staffPerSqm: 15.0
}, {
  id: 'muenchen-niederlassung',
  name: 'BMW Niederlassung München',
  region: 'Deutschland',
  rating: 4.6,
  staff: 32,
  size: 1800,
  staffPerSqm: 17.8
}, {
  id: 'nuernberg-niederlassung',
  name: 'BMW Niederlassung Nürnberg',
  region: 'Deutschland',
  rating: 4.3,
  staff: 28,
  size: 1700,
  staffPerSqm: 16.5
}, {
  id: 'dresden-niederlassung',
  name: 'BMW Niederlassung Dresden',
  region: 'Deutschland',
  rating: 4.1,
  staff: 19,
  size: 1250,
  staffPerSqm: 15.2
}, {
  id: 'stuttgart-niederlassung',
  name: 'BMW Niederlassung Stuttgart',
  region: 'Deutschland',
  rating: 4.4,
  staff: 27,
  size: 1650,
  staffPerSqm: 16.4
}, {
  id: 'frankfurt-niederlassung',
  name: 'BMW Niederlassung Frankfurt',
  region: 'Deutschland',
  rating: 4.2,
  staff: 23,
  size: 1500,
  staffPerSqm: 15.3
}, {
  id: 'manhattan-bmw',
  name: 'BMW of Manhattan, New York',
  region: 'USA',
  rating: 3.5,
  staff: 14,
  size: 1100,
  staffPerSqm: 12.7
}, {
  id: 'beverly-hills-bmw',
  name: 'BMW of Beverly Hills',
  region: 'USA',
  rating: 4.4,
  staff: 28,
  size: 1600,
  staffPerSqm: 17.5
}, {
  id: 'chicago-bmw',
  name: 'BMW of Chicago',
  region: 'USA',
  rating: 3.7,
  staff: 15,
  size: 1050,
  staffPerSqm: 14.3
}, {
  id: 'dallas-bmw',
  name: 'BMW of Dallas',
  region: 'USA',
  rating: 4.1,
  staff: 22,
  size: 1400,
  staffPerSqm: 15.7
}, {
  id: 'miami-bmw',
  name: 'BMW of Miami',
  region: 'USA',
  rating: 4.3,
  staff: 25,
  size: 1500,
  staffPerSqm: 16.7
}, {
  id: 'beijing-bmw',
  name: 'BMW Center Beijing',
  region: 'China',
  rating: 3.9,
  staff: 20,
  size: 1400,
  staffPerSqm: 14.3
}, {
  id: 'shenyang-bmw',
  name: 'BMW Brilliance Shenyang',
  region: 'China',
  rating: 4.5,
  staff: 38,
  size: 2200,
  staffPerSqm: 17.3
}, {
  id: 'shanghai-bmw',
  name: "BMW Shanghai Jing'an",
  region: 'China',
  rating: 3.6,
  staff: 16,
  size: 1300,
  staffPerSqm: 12.3
}, {
  id: 'guangzhou-bmw',
  name: 'BMW Guangzhou Tianhe',
  region: 'China',
  rating: 4.0,
  staff: 21,
  size: 1350,
  staffPerSqm: 15.6
}, {
  id: 'chengdu-bmw',
  name: 'BMW Chengdu Jinniu',
  region: 'China',
  rating: 4.2,
  staff: 25,
  size: 1550,
  staffPerSqm: 16.1
}, {
  id: 'london-park-lane',
  name: 'BMW Park Lane, London',
  region: 'Großbritannien',
  rating: 4.2,
  staff: 24,
  size: 1500,
  staffPerSqm: 16.0
}, {
  id: 'manchester-bmw',
  name: 'BMW Manchester Deansgate',
  region: 'Großbritannien',
  rating: 3.8,
  staff: 16,
  size: 1100,
  staffPerSqm: 14.5
}, {
  id: 'birmingham-bmw',
  name: 'BMW Birmingham Broad Street',
  region: 'Großbritannien',
  rating: 4.0,
  staff: 18,
  size: 1200,
  staffPerSqm: 15.0
}, {
  id: 'edinburgh-bmw',
  name: 'BMW Edinburgh',
  region: 'Großbritannien',
  rating: 4.3,
  staff: 16,
  size: 950,
  staffPerSqm: 16.8
}, {
  id: 'paris-champs',
  name: 'BMW Paris Champs-Élysées',
  region: 'Frankreich',
  rating: 4.1,
  staff: 21,
  size: 1350,
  staffPerSqm: 15.6
}, {
  id: 'lyon-bmw',
  name: 'BMW Lyon Part-Dieu',
  region: 'Frankreich',
  rating: 3.9,
  staff: 14,
  size: 1000,
  staffPerSqm: 14.0
}, {
  id: 'marseille-bmw',
  name: 'BMW Marseille Prado',
  region: 'Frankreich',
  rating: 3.7,
  staff: 13,
  size: 950,
  staffPerSqm: 13.7
}, {
  id: 'bordeaux-bmw',
  name: 'BMW Bordeaux Mériadeck',
  region: 'Frankreich',
  rating: 4.2,
  staff: 16,
  size: 1000,
  staffPerSqm: 16.0
}, {
  id: 'sydney-bmw',
  name: 'BMW Sydney Rushcutters Bay',
  region: 'Australien',
  rating: 4.4,
  staff: 27,
  size: 1650,
  staffPerSqm: 16.4
}, {
  id: 'melbourne-bmw',
  name: 'BMW Melbourne Collins Street',
  region: 'Australien',
  rating: 4.2,
  staff: 25,
  size: 1550,
  staffPerSqm: 16.1
}, {
  id: 'brisbane-bmw',
  name: 'BMW Brisbane Fortitude Valley',
  region: 'Australien',
  rating: 4.0,
  staff: 18,
  size: 1200,
  staffPerSqm: 15.0
}, {
  id: 'perth-bmw',
  name: 'BMW Perth Osborne Park',
  region: 'Australien',
  rating: 3.8,
  staff: 15,
  size: 1050,
  staffPerSqm: 14.3
}, {
  id: 'tokyo-bmw',
  name: 'BMW Tokyo Aoyama',
  region: 'Japan',
  rating: 4.0,
  staff: 19,
  size: 1300,
  staffPerSqm: 14.6
}, {
  id: 'osaka-bmw',
  name: 'BMW Osaka Midosuji',
  region: 'Japan',
  rating: 3.9,
  staff: 16,
  size: 1150,
  staffPerSqm: 13.9
}, {
  id: 'nagoya-bmw',
  name: 'BMW Nagoya Sakae',
  region: 'Japan',
  rating: 4.1,
  staff: 18,
  size: 1200,
  staffPerSqm: 15.0
}, {
  id: 'fukuoka-bmw',
  name: 'BMW Fukuoka Tenjin',
  region: 'Japan',
  rating: 3.7,
  staff: 14,
  size: 1000,
  staffPerSqm: 14.0
}];
// Land aggregated data
const regionsData = [{
  id: 'deutschland',
  name: 'Deutschland',
  rating: 4.2,
  staff: 142,
  size: 8750,
  staffPerSqm: 16.2,
  markets: 5
}, {
  id: 'usa',
  name: 'USA',
  rating: 3.95,
  staff: 42,
  size: 2700,
  staffPerSqm: 15.6,
  markets: 2
}, {
  id: 'china',
  name: 'China',
  rating: 4.2,
  staff: 58,
  size: 3600,
  staffPerSqm: 16.1,
  markets: 2
}, {
  id: 'uk',
  name: 'Großbritannien',
  rating: 4.2,
  staff: 24,
  size: 1500,
  staffPerSqm: 16.0,
  markets: 1
}, {
  id: 'frankreich',
  name: 'Frankreich',
  rating: 4.1,
  staff: 21,
  size: 1350,
  staffPerSqm: 15.6,
  markets: 1
}, {
  id: 'australien',
  name: 'Australien',
  rating: 4.3,
  staff: 52,
  size: 3200,
  staffPerSqm: 16.3,
  markets: 2
}, {
  id: 'japan',
  name: 'Japan',
  rating: 3.95,
  staff: 35,
  size: 2450,
  staffPerSqm: 14.3,
  markets: 2
}];
// Konzern data
const konzernData = {
  id: 'konzern',
  name: 'BMW Gesamtnetz Weltweit',
  rating: 4.15,
  staff: 374,
  size: 24550,
  staffPerSqm: 15.9,
  markets: 15,
  regions: 7
};
// Time series data
const timeSeriesData = [{
  month: 'Feb 23',
  rating: 4.05,
  staffPerSqm: 14.8
}, {
  month: 'Mär 23',
  rating: 4.08,
  staffPerSqm: 15.0
}, {
  month: 'Apr 23',
  rating: 4.06,
  staffPerSqm: 14.9
}, {
  month: 'Mai 23',
  rating: 4.1,
  staffPerSqm: 15.2
}, {
  month: 'Jun 23',
  rating: 4.12,
  staffPerSqm: 15.4
}, {
  month: 'Jul 23',
  rating: 4.09,
  staffPerSqm: 15.3
}, {
  month: 'Aug 23',
  rating: 4.11,
  staffPerSqm: 15.5
}, {
  month: 'Sep 23',
  rating: 4.14,
  staffPerSqm: 15.6
}, {
  month: 'Okt 23',
  rating: 4.13,
  staffPerSqm: 15.7
}, {
  month: 'Nov 23',
  rating: 4.16,
  staffPerSqm: 15.8
}, {
  month: 'Dez 23',
  rating: 4.18,
  staffPerSqm: 15.9
}, {
  month: 'Jan 24',
  rating: 4.2,
  staffPerSqm: 15.9
}];
// Calculate correlation coefficient
const calculateCorrelation = (data: Array<{
  rating: number;
  staffPerSqm: number;
}>) => {
  const n = data.length;
  const sumX = data.reduce((acc, d) => acc + d.staffPerSqm, 0);
  const sumY = data.reduce((acc, d) => acc + d.rating, 0);
  const sumXY = data.reduce((acc, d) => acc + d.staffPerSqm * d.rating, 0);
  const sumX2 = data.reduce((acc, d) => acc + d.staffPerSqm * d.staffPerSqm, 0);
  const sumY2 = data.reduce((acc, d) => acc + d.rating * d.rating, 0);
  const numerator = n * sumXY - sumX * sumY;
  const denominator = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));
  return denominator === 0 ? 0 : numerator / denominator;
};
// Custom tooltip for scatter chart
const CustomScatterTooltip = ({
  active,
  payload
}: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200 text-sm" data-id="element-1094">
        <div className="font-semibold text-gray-900 mb-2" data-id="element-1095">{data.name}</div>
        <div className="space-y-1" data-id="element-1096">
          <div className="flex justify-between gap-4" data-id="element-1097">
            <span className="text-gray-500" data-id="element-1098">Rating:</span>
            <span className="font-medium" data-id="element-1099">{data.rating.toFixed(1)} ★</span>
          </div>
          <div className="flex justify-between gap-4" data-id="element-1100">
            <span className="text-gray-500" data-id="element-1101">Berater/1000m²:</span>
            <span className="font-medium" data-id="element-1102">{data.staffPerSqm.toFixed(1)}</span>
          </div>
          <div className="flex justify-between gap-4" data-id="element-1103">
            <span className="text-gray-500" data-id="element-1104">Mitarbeiter:</span>
            <span className="font-medium" data-id="element-1105">{data.staff}</span>
          </div>
          <div className="flex justify-between gap-4" data-id="element-1106">
            <span className="text-gray-500" data-id="element-1107">Fläche:</span>
            <span className="font-medium" data-id="element-1108">
              {data.size.toLocaleString('de-DE')} m²
            </span>
          </div>
        </div>
      </div>;
  }
  return null;
};
export function PersonalReputationPage() {
  const [selectedLevel, setSelectedLevel] = useState<string>('konzern');
  // Get data based on selected level
  const getScatterData = () => {
    if (selectedLevel === 'konzern') return marketsData.map(m => ({
      ...m,
      z: m.size
    }));
    if (selectedLevel === 'markt') return marketsData.map(m => ({
      ...m,
      z: m.size
    }));
    const countryName = countries.find(c => c.id === selectedLevel)?.name;
    return marketsData.filter(m => m.region === countryName).map(m => ({
      ...m,
      z: m.size
    }));
  };
  const scatterData = getScatterData();
  const correlation = calculateCorrelation(scatterData);
  // Calculate averages
  const avgStaffPerSqm = scatterData.reduce((acc, d) => acc + d.staffPerSqm, 0) / scatterData.length;
  const avgRating = scatterData.reduce((acc, d) => acc + d.rating, 0) / scatterData.length;
  // Find understaffed markets (low staff, low rating)
  const understaffed = [...scatterData].filter(d => d.staffPerSqm < avgStaffPerSqm && d.rating < avgRating).sort((a, b) => a.staffPerSqm - b.staffPerSqm).slice(0, 3);
  // Find well-staffed high performers (high staff, high rating)
  const topPerformers = [...scatterData].filter(d => d.staffPerSqm >= avgStaffPerSqm).sort((a, b) => b.rating - a.rating).slice(0, 3);
  // Calculate potential improvement
  const potentialImprovement = understaffed.reduce((acc, d) => {
    const staffGap = avgStaffPerSqm - d.staffPerSqm;
    const estimatedRatingGain = staffGap * 0.08;
    return acc + estimatedRatingGain;
  }, 0) / understaffed.length || 0;
  return <div className="p-6 space-y-6 overflow-y-auto h-[calc(100vh-64px)]" data-id="element-1109">
      {/* Level Filter */}
      <Card className="!p-4" data-id="element-1110">
        <div className="flex items-center gap-4 flex-wrap" data-id="element-1111">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-1112">
            <Filter size={16} data-id="element-1113" />
            <span data-id="element-1114">Ebene:</span>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-1115">
            <button onClick={() => setSelectedLevel('konzern')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${selectedLevel === 'konzern' ? 'text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} style={selectedLevel === 'konzern' ? {
            backgroundColor: '#1C69D4'
          } : {}} data-id="element-1116">
              <Building2 size={14} data-id="element-1117" />
              Konzern-Übersicht
            </button>

            <div className="flex items-center" data-id="element-1118">
              <div className="w-px h-6 bg-gray-200 mx-1" data-id="element-1119" />
            </div>

            {countries.map(country => <button key={country.id} onClick={() => setSelectedLevel(country.id)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${selectedLevel === country.id ? 'text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} style={selectedLevel === country.id ? {
            backgroundColor: '#1C69D4'
          } : {}} data-id="element-1120">
                {country.name}
              </button>)}
          </div>
        </div>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1121">
        <KPICard title="Korrelation Personal ↔ Rating" value={correlation.toFixed(2)} change={correlation > 0.6 ? 'Stark positiv' : correlation > 0.3 ? 'Moderat' : 'Schwach'} trend="up" icon={<TrendingUp size={18} data-id="element-1123" />} subtext="Pearson-Koeffizient" data-id="element-1122" />
        <KPICard title="Ø Berater pro Showroom" value={avgStaffPerSqm.toFixed(1)} change="+1.2 vs. Vorjahr" trend="up" icon={<Users size={18} data-id="element-1125" />} subtext="Mitarbeiter/1000m²" data-id="element-1124" />
        <KPICard title="Ø Bewertung" value={avgRating.toFixed(2)} change="+0.15" trend="up" icon={<Star size={18} data-id="element-1127" />} subtext="Google Rating" data-id="element-1126" />
        <KPICard title="Verbesserungspotenzial" value={`+${potentialImprovement.toFixed(2)}★`} change={`${understaffed.length} Autohäuser`} trend="up" icon={<UserCheck size={18} data-id="element-1129" />} subtext="bei Ø-Besetzung" data-id="element-1128" />
      </div>

      {/* Main Scatter Chart */}
      <Card data-id="element-1130">
        <div className="p-6" data-id="element-1131">
          <div className="flex items-center justify-between mb-6" data-id="element-1132">
            <div data-id="element-1133">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-1134">
                Personalbesetzung vs. Kundenbewertung
              </h3>
              <p className="text-sm text-gray-500" data-id="element-1135">
                Blasengröße = Showroomfläche (m²) • Berater pro 1000m²
                Showroomfläche
              </p>
            </div>
            <div className="flex items-center gap-4 text-xs text-gray-500" data-id="element-1136">
              <div className="flex items-center gap-1" data-id="element-1137">
                <div className="w-3 h-3 rounded-full" style={{
                backgroundColor: '#639754'
              }} data-id="element-1138"></div>
                <span data-id="element-1139">Gut besetzt</span>
              </div>
              <div className="flex items-center gap-1" data-id="element-1140">
                <div className="w-3 h-3 rounded-full" style={{
                backgroundColor: '#E03C32'
              }} data-id="element-1141"></div>
                <span data-id="element-1142">Unterbesetzt</span>
              </div>
            </div>
          </div>
          <div className="h-96" data-id="element-1143">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1144">
              <ScatterChart margin={{
              top: 20,
              right: 20,
              bottom: 20,
              left: 20
            }} data-id="element-1145">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-1146" />
                <XAxis type="number" dataKey="staffPerSqm" name="Berater/1000m²" domain={[12, 19]} axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} label={{
                value: 'Berater pro 1000m² Showroomfläche',
                position: 'bottom',
                offset: 0,
                style: {
                  fontSize: 11,
                  fill: '#6b7280'
                }
              }} data-id="element-1147" />
                <YAxis type="number" dataKey="rating" name="Rating" domain={[3.2, 5]} axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} label={{
                value: 'Google Rating (Sterne)',
                angle: -90,
                position: 'insideLeft',
                style: {
                  fontSize: 11,
                  fill: '#6b7280'
                }
              }} data-id="element-1148" />
                <ZAxis type="number" dataKey="z" range={[100, 800]} data-id="element-1149" />
                <Tooltip content={<CustomScatterTooltip data-id="element-1151" />} data-id="element-1150" />
                <Scatter name="Autohäuser" data={scatterData} fill="#1C69D4" data-id="element-1152">
                  {scatterData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.staffPerSqm >= avgStaffPerSqm ? '#639754' : entry.staffPerSqm >= avgStaffPerSqm - 1.5 ? '#F59E0B' : '#E03C32'} fillOpacity={0.7} data-id="element-1153" />)}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-sm" data-id="element-1154">
            <div className="flex items-center gap-2" data-id="element-1155">
              <TrendingUp size={16} style={{
              color: '#639754'
            }} data-id="element-1156" />
              <span className="text-gray-600" data-id="element-1157">
                Korrelation:{' '}
                <span className="font-semibold" style={{
                color: correlation > 0.5 ? '#639754' : '#F59E0B'
              }} data-id="element-1158">
                  {(correlation * 100).toFixed(0)}%
                </span>{' '}
                – Mehr Personal korreliert mit besseren Bewertungen
              </span>
            </div>
            <div className="text-gray-500" data-id="element-1159">
              Ø Besetzung: {avgStaffPerSqm.toFixed(1)} Berater/1000m²
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1160">
        {/* Top Performers */}
        <Card data-id="element-1161">
          <div className="p-6" data-id="element-1162">
            <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2" data-id="element-1163">
              <ArrowUpRight className="text-green-600" size={20} data-id="element-1164" />
              Top Performer: Gut besetzt & hohe Bewertung
            </h3>
            <p className="text-sm text-gray-500 mb-4" data-id="element-1165">
              Autohäuser mit überdurchschnittlicher Besetzung und Rating
            </p>
            <div className="space-y-3" data-id="element-1166">
              {topPerformers.length > 0 ? topPerformers.map((item, index) => <motion.div key={item.id} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.1
            }} className="flex items-center justify-between p-3 rounded-lg" style={{
              backgroundColor: '#e8f0e5'
            }} data-id="element-1167">
                    <div className="flex items-center gap-3" data-id="element-1168">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{
                  backgroundColor: '#639754'
                }} data-id="element-1169">
                        {index + 1}
                      </div>
                      <div data-id="element-1170">
                        <div className="font-medium text-gray-900 text-sm" data-id="element-1171">
                          {item.name}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-1172">
                          {item.staff} Mitarb. •{' '}
                          {item.size.toLocaleString('de-DE')} m²
                        </div>
                      </div>
                    </div>
                    <div className="text-right" data-id="element-1173">
                      <div className="font-bold text-lg" style={{
                  color: '#639754'
                }} data-id="element-1174">
                        {item.rating.toFixed(1)} ★
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-1175">
                        {item.staffPerSqm.toFixed(1)} Berater/1000m²
                      </div>
                    </div>
                  </motion.div>) : <div className="text-center py-8 text-gray-500" data-id="element-1176">
                  Keine Daten verfügbar
                </div>}
            </div>
            {topPerformers.length > 0 && <div className="mt-4 p-3 bg-green-50 rounded-lg" data-id="element-1177">
                <div className="text-sm text-green-800" data-id="element-1178">
                  <strong data-id="element-1179">Erkenntnis:</strong> Diese Autohäuser zeigen, dass
                  eine Besetzung von{' '}
                  <strong data-id="element-1180">
                    {(topPerformers.reduce((acc, d) => acc + d.staffPerSqm, 0) / topPerformers.length).toFixed(1)}{' '}
                    Berater/1000m²
                  </strong>{' '}
                  mit Ratings von{' '}
                  <strong data-id="element-1181">
                    {(topPerformers.reduce((acc, d) => acc + d.rating, 0) / topPerformers.length).toFixed(1)}
                    + Sternen
                  </strong>{' '}
                  einhergeht.
                </div>
              </div>}
          </div>
        </Card>

        {/* Understaffed */}
        <Card data-id="element-1182">
          <div className="p-6" data-id="element-1183">
            <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2" data-id="element-1184">
              <AlertTriangle className="text-red-600" size={20} data-id="element-1185" />
              Handlungsbedarf: Unterbesetzte Autohäuser
            </h3>
            <p className="text-sm text-gray-500 mb-4" data-id="element-1186">
              Autohäuser mit unterdurchschnittlicher Besetzung und Rating
            </p>
            <div className="space-y-3" data-id="element-1187">
              {understaffed.length > 0 ? understaffed.map((item, index) => <motion.div key={item.id} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.1
            }} className="flex items-center justify-between p-3 rounded-lg" style={{
              backgroundColor: '#fce8e7'
            }} data-id="element-1188">
                    <div className="flex items-center gap-3" data-id="element-1189">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{
                  backgroundColor: '#E03C32'
                }} data-id="element-1190">
                        !
                      </div>
                      <div data-id="element-1191">
                        <div className="font-medium text-gray-900 text-sm" data-id="element-1192">
                          {item.name}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-1193">
                          {item.staff} Mitarb. •{' '}
                          {item.size.toLocaleString('de-DE')} m²
                        </div>
                      </div>
                    </div>
                    <div className="text-right" data-id="element-1194">
                      <div className="font-bold text-lg" style={{
                  color: '#E03C32'
                }} data-id="element-1195">
                        {item.rating.toFixed(1)} ★
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-1196">
                        {item.staffPerSqm.toFixed(1)} Berater/1000m² (
                        {((avgStaffPerSqm - item.staffPerSqm) / avgStaffPerSqm * 100).toFixed(0)}
                        % unter Ø)
                      </div>
                    </div>
                  </motion.div>) : <div className="text-center py-8 text-gray-500" data-id="element-1197">
                  Keine unterbesetzten Autohäuser gefunden
                </div>}
            </div>
            {understaffed.length > 0 && <div className="mt-4 p-3 bg-blue-50 rounded-lg" data-id="element-1198">
                <div className="text-sm text-blue-800" data-id="element-1199">
                  <strong data-id="element-1200">Empfehlung:</strong> Bei Erhöhung auf Ø-Besetzung (
                  {avgStaffPerSqm.toFixed(1)} Berater/1000m²) ist eine
                  Rating-Verbesserung von ca.{' '}
                  <strong data-id="element-1201">+{potentialImprovement.toFixed(2)} Sternen</strong> zu
                  erwarten.
                </div>
              </div>}
          </div>
        </Card>
      </div>

      {/* Time Series Chart */}
      <Card data-id="element-1202">
        <div className="p-6" data-id="element-1203">
          <div className="flex items-center justify-between mb-6" data-id="element-1204">
            <div data-id="element-1205">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-1206">
                Entwicklung über Zeit
              </h3>
              <p className="text-sm text-gray-500" data-id="element-1207">
                Personalbesetzung und Kundenbewertung (12 Monate)
              </p>
            </div>
            <div className="flex items-center gap-4 text-sm" data-id="element-1208">
              <div className="flex items-center gap-2" data-id="element-1209">
                <div className="w-3 h-3 rounded-full" style={{
                backgroundColor: '#1C69D4'
              }} data-id="element-1210"></div>
                <span className="text-gray-600" data-id="element-1211">Berater/1000m²</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-1212">
                <div className="w-6 h-0.5" style={{
                backgroundColor: '#F59E0B'
              }} data-id="element-1213"></div>
                <span className="text-gray-600" data-id="element-1214">Rating</span>
              </div>
            </div>
          </div>
          <div className="h-72" data-id="element-1215">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1216">
              <ComposedChart data={timeSeriesData} data-id="element-1217">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} data-id="element-1218" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} data-id="element-1219" />
                <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} domain={[14, 17]} label={{
                value: 'Berater/1000m²',
                angle: -90,
                position: 'insideLeft',
                style: {
                  fontSize: 11,
                  fill: '#9ca3af'
                }
              }} data-id="element-1220" />
                <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} domain={[3.9, 4.4]} label={{
                value: 'Rating',
                angle: 90,
                position: 'insideRight',
                style: {
                  fontSize: 11,
                  fill: '#9ca3af'
                }
              }} data-id="element-1221" />
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontSize: '12px'
              }} formatter={(value: number, name: string) => [name === 'staffPerSqm' ? `${value.toFixed(1)} Berater/1000m²` : value.toFixed(2), name === 'staffPerSqm' ? 'Personal' : 'Rating']} data-id="element-1222" />
                <Bar yAxisId="left" dataKey="staffPerSqm" fill="#1C69D4" radius={[4, 4, 0, 0]} barSize={30} data-id="element-1223" />
                <Line yAxisId="right" type="monotone" dataKey="rating" stroke="#F59E0B" strokeWidth={3} dot={{
                fill: '#F59E0B',
                strokeWidth: 2,
                r: 4
              }} data-id="element-1224" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-4 text-center" data-id="element-1225">
            <div data-id="element-1226">
              <div className="text-xs text-gray-500 mb-1" data-id="element-1227">
                Personalzuwachs (12 Monate)
              </div>
              <div className="text-lg font-bold" style={{
              color: '#639754'
            }} data-id="element-1228">
                +7.4%
              </div>
            </div>
            <div data-id="element-1229">
              <div className="text-xs text-gray-500 mb-1" data-id="element-1230">
                Rating-Verbesserung (12 Monate)
              </div>
              <div className="text-lg font-bold" style={{
              color: '#639754'
            }} data-id="element-1231">
                +0.15★
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Data Table */}
      <Card data-id="element-1232">
        <div className="p-6" data-id="element-1233">
          <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1234">
            Detailübersicht{' '}
            {selectedLevel === 'markt' ? 'Autohäuser' : selectedLevel === 'konzern' ? 'Konzern' : countries.find(c => c.id === selectedLevel)?.name || ''}
          </h3>
          <div className="overflow-x-auto" data-id="element-1235">
            <table className="w-full text-sm" data-id="element-1236">
              <thead data-id="element-1237">
                <tr className="border-b border-gray-200" data-id="element-1238">
                  <th className="text-left py-3 px-4 font-medium text-gray-500" data-id="element-1239">
                    Name
                  </th>
                  {selectedLevel === 'markt' && <th className="text-left py-3 px-4 font-medium text-gray-500" data-id="element-1240">
                      Land
                    </th>}
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1241">
                    Rating
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1242">
                    Personal
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1243">
                    Showroom
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1244">
                    Berater/1000m²
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1245">
                    vs. Ø
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1246">
                {scatterData.map((item: any, index) => {
                const staffDiff = (item.staffPerSqm - avgStaffPerSqm) / avgStaffPerSqm * 100;
                return <tr key={item.id || index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-1247">
                      <td className="py-3 px-4 font-medium text-gray-900" data-id="element-1248">
                        {item.name}
                      </td>
                      {selectedLevel === 'markt' && <td className="py-3 px-4 text-gray-500" data-id="element-1249">
                          {item.region}
                        </td>}
                      <td className="py-3 px-4 text-right" data-id="element-1250">
                        <span className={`font-medium ${item.rating >= 4.3 ? 'text-green-600' : item.rating >= 3.8 ? 'text-gray-900' : 'text-red-600'}`} data-id="element-1251">
                          {item.rating.toFixed(1)} ★
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right text-gray-900" data-id="element-1252">
                        {item.staff} MA
                      </td>
                      <td className="py-3 px-4 text-right text-gray-500" data-id="element-1253">
                        {item.size.toLocaleString('de-DE')} m²
                      </td>
                      <td className="py-3 px-4 text-right" data-id="element-1254">
                        <span className={`font-medium ${staffDiff >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-1255">
                          {item.staffPerSqm.toFixed(1)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right" data-id="element-1256">
                        <span className={`font-medium ${staffDiff >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-1257">
                          {staffDiff >= 0 ? '+' : ''}
                          {staffDiff.toFixed(1)}%
                        </span>
                      </td>
                    </tr>;
              })}
              </tbody>
            </table>
          </div>
        </div>
      </Card>
    </div>;
}